import { Box, Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { handleUnauthorizedResponse } from '../../../api/http';
import RenewalsApi from '../../../api/RenewalsApi';
import { AuthData } from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { RenewalResponse } from '../../../types/renewals';
import {
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../../common';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import RenewalsTable from '../../RenewalsTable';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
	errorData: ErrorData;
}

/**
 * The list of renewals on a policy
 */
const RenewalsList = (props: Props) => {
	const { authData, policyData, errorData } = props;

	const { policy } = policyData;

	const [renewals, setRenewals] = useState<RenewalResponse[]>([]);
	const [loading, setLoading] = useState(true);

	const [alignment, setAlignment] = useState('Active');
	const [filteredRenewals, setFilteredRenewals] = useState(
		renewals.filter(
			(e) => e.state !== 'invalidated' && e.state !== 'discarded',
		),
	);

	const handleChange = (
		event: React.MouseEvent<HTMLElement>,
		newAlignment: string,
	) => {
		if (newAlignment) {
			setAlignment(newAlignment);
			if (alignment == 'All') {
				setFilteredRenewals(
					renewals.filter(
						(e) => e.state !== 'invalidated' && e.state !== 'discarded',
					),
				);
			} else {
				setFilteredRenewals(renewals.filter((e) => e.state !== 'discarded'));
			}
		}
	};

	useEffect(() => {
		const source = axios.CancelToken.source();
		async function load() {
			try {
				const response = await RenewalsApi.getRenewalsForPolicy(
					authData.auth.authorizationToken,
					policy.locator,
					{ cancelToken: source.token },
				);
				if (response) {
					setRenewals(response.data);
					setFilteredRenewals(
						response.data.filter(
							(e) => e.state !== 'invalidated' && e.state !== 'discarded',
						),
					);
				}
			} catch (error) {
				if (axios.isCancel(error)) {
					return;
				} else {
					errorData.handleErrorResponse(error, `Failed to fetch renewal`);
					if (await handleUnauthorizedResponse(error, authData)) {
						return;
					}
				}
			}
			setLoading(false);
		}
		load();

		return () => {
			source.cancel();
		};
	}, []);

	if (loading) {
		return <LargeCenterSpinner />;
	}

	return (
		<>
			<Grid container spacing={2}>
				<Grid item xs={8}>
					<StyledTitleContainer>
						<StyledTitle>{'Renewals'}</StyledTitle>
					</StyledTitleContainer>
				</Grid>
				<Grid item xs={4}>
					<Box display='flex' justifyContent='flex-end'>
						<ToggleButtonGroup
							color='primary'
							value={alignment}
							exclusive
							onChange={handleChange}
						>
							<ToggleButton
								value='Active'
								style={{
									color: 'white',
									height: '40px',
									backgroundColor:
										(alignment == 'Active' && '#314974') ||
										(alignment != 'Active' && '#6B778C'),
								}}
							>
								Active
							</ToggleButton>
							<ToggleButton
								value='All'
								style={{
									color: 'white',
									height: '40px',
									backgroundColor:
										(alignment == 'All' && '#314974') ||
										(alignment != 'All' && '#6B778C'),
								}}
							>
								All
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>
				</Grid>
			</Grid>

			<hr />
			<StyledSectionContainer>
				<RenewalsTable renewals={filteredRenewals} />
			</StyledSectionContainer>
		</>
	);
};

export default withPolicyContext<Props>(RenewalsList);
